import React, { useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
  FormErrors,
} from '@ui-framework/index';
import { toMediumDate } from '@utils/Dated';

import {
  IconLibrary,
  TimeSlotTypeChipWithAfterBeforeHours,
  useAvailabilitySlotTypes,
} from '@product-framework/index';
import { nanoid } from 'nanoid';
import { CustomerServiceRequest } from '../../../FormFields';
import { useServiceProvider } from '../../../../providers/ServiceProviderProvider';

function CSRForm({
  onClose,
  location,
  serviceJob,
  isAssign,
  isReAssign,
  dispatchEvent,
}) {
  const { serviceProvider } = useServiceProvider();
  const { formatMessage } = useLocalization();
  const { slotTypes } = useAvailabilitySlotTypes();
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState([]);

  const { customerServiceRequests = [] } = serviceJob;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, post } = useDataProvider(provider);

  const countOfCSR = Array.isArray(customerServiceRequests)
    ? customerServiceRequests?.length
    : 0;

  const dateOptions =
    customerServiceRequests?.map((date, idx) => ({
      label: (
        <Stack direction="row" spacing={1}>
          <Typography>{toMediumDate(date?.requestDate)}</Typography>
          <TimeSlotTypeChipWithAfterBeforeHours
            afterHour={date?.afterHour}
            beforeHour={date?.beforeHour}
            code={date?.slotTypeCode}
            smallAfterAndBefore
          />
        </Stack>
      ),
      value: `csr_${idx}`,
    })) || [];

  let schema;

  if (countOfCSR === 0) {
    schema = yup.object().shape({
      appointmentDate: yup.mixed().test(
        'request-date-required-not-in-past',
        formatMessage({
          id: 'DATE_IS_REQUIRED',
          defaultMessage: 'Date is required',
        }),
        (value, context) => {
          if (!value?.requestDate) {
            return context.createError({
              path: `${context.path}.requestDate`,
              message: formatMessage({
                id: 'DATE_IS_REQUIRED',
                defaultMessage: 'Date is required',
              }),
            });
          }
          if (dayjs(value?.requestDate).isBefore(dayjs(), 'day')) {
            return context.createError({
              path: `${context.path}.requestDate`,
              message: formatMessage({
                id: 'DATE_CANNOT_BE_IN_THE_PAST',
                defaultMessage: 'Date cannot be in the past',
              }),
            });
          }
          return true;
        },
      ),
    });
  } else {
    schema = yup.object().shape({
      selectedDateOption: yup.string().required(
        formatMessage({
          id: 'PLEASE_SELECT_A_DATE_OPTION',
          defaultMessage: 'Please select a date option',
        }),
      ),
      appointmentDate: yup.mixed().when('selectedDateOption', {
        is: 'custom',
        then: s =>
          s.test(
            'request-date-required-not-in-past',
            formatMessage({
              id: 'DATE_IS_REQUIRED',
              defaultMessage: 'Date is required',
            }),
            (value, context) => {
              if (!value?.requestDate) {
                return context.createError({
                  path: `${context.path}.requestDate`,
                  message: formatMessage({
                    id: 'DATE_IS_REQUIRED',
                    defaultMessage: 'Date is required',
                  }),
                });
              }
              if (dayjs(value).isBefore(dayjs(), 'day')) {
                return context.createError({
                  path: `${context.path}.requestDate`,
                  message: formatMessage({
                    id: 'DATE_CANNOT_BE_IN_THE_PAST',
                    defaultMessage: 'Date cannot be in the past',
                  }),
                });
              }
              return true;
            },
          ),
        otherwise: s => s.notRequired(),
      }),
    });
  }

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      selectedDateOption: countOfCSR === 0 ? 'custom' : 'csr_0',
      appointmentDate: { requestDate: null, slotTypeCode: slotTypes[0].code },
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = methods;

  const selectedDateOption = watch('selectedDateOption');
  const showCustomInputs = countOfCSR === 0 || selectedDateOption === 'custom';

  async function onFormSubmit(data) {
    setLoading(true);
    setApiErrors([]);

    let body;

    if (countOfCSR === 0 || data.selectedDateOption === 'custom') {
      body = {
        serviceDate: data.appointmentDate?.requestDate,
        slotTypeCode: data.appointmentDate?.slotTypeCode,
        jobRecordVersion: serviceJob.recordVersion,
        dispatchEventId: dispatchEvent?.id,
      };
    } else {
      const idx = parseInt(data.selectedDateOption.replace('csr_', ''), 10);
      const csr = customerServiceRequests[idx];
      body = {
        serviceDate: csr.requestDate,
        slotTypeCode: csr?.slotTypeCode,
        jobRecordVersion: serviceJob.recordVersion,
        dispatchEventId: dispatchEvent?.id,
      };
    }

    const queryParams = new URLSearchParams({
      reassignFlag: isReAssign,
    });

    if (location?.id) {
      queryParams.set('locationId', String(location?.id));
    }

    const response = await post(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProvider?.id}/pending-acceptance/${serviceJob?.id}/assign?${queryParams.toString()}`,
      body,
    );
    if (response.ok) {
      if (isAssign) {
        snackbar.showMessage(
          formatMessage({
            id: 'JOB_WAS_ASSIGNED_SUCCESSFULLY',
            defaultMessage: 'Job was assigned successfully',
          }),
        );
      }
      if (isReAssign) {
        snackbar.showMessage(
          formatMessage({
            id: 'JOB_WAS_REASSIGNED_SUCCESSFULLY',
            defaultMessage: 'Job was reassigned successfully',
          }),
        );
      }
      onClose(nanoid());
    } else {
      setApiErrors(response?.errors);
    }
    setLoading(false);
  }

  const buttons = [];
  if (isAssign) {
    buttons.push({
      label: formatMessage({ id: 'ASSIGN', defaultMessage: 'Assign' }),
      type: 'submit',
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Yes />,
    });
  }
  if (isReAssign) {
    buttons.push({
      label: formatMessage({ id: 'REASSIGN', defaultMessage: 'Reassign' }),
      type: 'submit',
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Yes />,
    });
  }
  buttons.push({
    label: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
    onClick: onClose,
    variant: 'contained',
    color: 'secondary',
    startIcon: <IconLibrary.Cancel />,
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogContent>
          {loading ? (
            <Box textAlign="center">
              <CircularProgress size="30px" />
              <Typography>
                <FormattedMessage id="PROCESSING" defaultMessage="Processing" />
              </Typography>
            </Box>
          ) : (
            <Box>
              <FormErrors errors={apiErrors} />
              {countOfCSR >= 1 && (
                <Controller
                  name="selectedDateOption"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      error={!!errors.selectedDateOption}
                      sx={{ width: '100%' }}>
                      <RadioGroup {...field}>
                        {dateOptions.map(opt => (
                          <FormControlLabel
                            key={opt.value}
                            value={opt.value}
                            control={<Radio />}
                            label={opt.label}
                          />
                        ))}
                        <FormControlLabel
                          value="custom"
                          control={<Radio />}
                          label={formatMessage({
                            id: 'OTHER',
                            defaultMessage: 'Other',
                          })}
                        />
                      </RadioGroup>
                      {errors.selectedDateOption && (
                        <FormHelperText>
                          {errors.selectedDateOption.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              )}
              {showCustomInputs && (
                <Box>
                  <Controller
                    name="appointmentDate"
                    control={control}
                    render={({ field }) => {
                      return (
                        <CustomerServiceRequest
                          field={{
                            dateComponentTitle: {
                              localeId: 'APPOINTMENT_DATE',
                              defaultMessage: 'Appointment Date',
                            },
                          }}
                          permission="WRITE"
                          {...field}
                        />
                      );
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', my: 2 }}>
          {buttons.map(({ label, ...btn }) => (
            <Button key={label} disabled={loading} {...btn}>
              {label}
            </Button>
          ))}
        </DialogActions>
      </form>
    </FormProvider>
  );
}

export default CSRForm;
